import { defineStore } from 'pinia';
import Vue from 'vue';
import { MESSAGE_TYPE, MESSAGE_STATUS } from "../api/enums.js"
import { getConversationLabels } from '../api/conversation.js'
import { getQuickReplyList } from '../api/quickReply.js'
import { getAutoReplyScripts, addAutoReplyScript, updateAutoReplyScript, deleteAutoReplyScript } from '../api/autoReplyScript.js'
import { getAutoFollowUpConfig } from '../api/autoFollowUp.js'
import { getMessagesBatch } from '../api/message.js'
import useUserStore from './userStore.js';
import localForage from 'localforage';
import { effectiveGroupID, extractParticipants } from '../utils/conversation.js';

let _saveDebounceTimer = null;
/**
 * 归并两个已按时间排序的消息数组
 */
function mergeSortedMessages(existing, newMsgs) {
	const getTime = m => m.sendTime || 0;
	const result = [];
	let i = 0, j = 0;
	while (i < existing.length && j < newMsgs.length) {
		if (getTime(existing[i]) <= getTime(newMsgs[j])) {
			result.push(existing[i++]);
		} else {
			result.push(newMsgs[j++]);
		}
	}
	while (i < existing.length) result.push(existing[i++]);
	while (j < newMsgs.length) result.push(newMsgs[j++]);
	return result;
}

/**
 * 根据消息类型生成会话列表预览文本
 */
function latestMsgPreview(msgType, content) {
	switch (msgType) {
		case MESSAGE_TYPE.TEXT:
			try {
				const parsed = JSON.parse(content);
				return parsed.content;
			} catch (e) {
				return content;
			}
		case MESSAGE_TYPE.IMAGE: return "[图片]";
		case MESSAGE_TYPE.GIF: return "[GIF]";
		case MESSAGE_TYPE.FILE: return "[文件]";
		case MESSAGE_TYPE.VIDEO: return "[视频]";
		case MESSAGE_TYPE.VOICE: return "[语音]";
		case MESSAGE_TYPE.CARD: return "[名片]";
		case MESSAGE_TYPE.MICRO_PROGRAM: return "[小程序]";
		case MESSAGE_TYPE.VIDEO_CHANNEL: return "[视频号]";
		case MESSAGE_TYPE.LINK: return "[链接]";
		case MESSAGE_TYPE.LOCATION: return "[位置]";
		case MESSAGE_TYPE.TEXT_MOON: return "[表情]";
		case MESSAGE_TYPE.AT:
		case MESSAGE_TYPE.GROUP_AT:
			try {
				const atContent = JSON.parse(content);
				return atContent.content || "[@消息]";
			} catch (e) {
				return "[@消息]";
			}
		case MESSAGE_TYPE.MERGE: return "[聊天记录]";
		case MESSAGE_TYPE.SYS: return "[系统消息]";
		case MESSAGE_TYPE.TIP_TEXT: return content;
		case MESSAGE_TYPE.FRIEND_DELETE_NOTIFY:
		case MESSAGE_TYPE.FRIEND_BLACK_TYPE:
		case MESSAGE_TYPE.FRIEND_DELETED_TYPE:
			try {
				const parsed = JSON.parse(content);
				return parsed.content || content;
			} catch (e) {
				return content;
			}
		default: return "[未知消息]";
	}
}


/**
 * 优化1(冷热消息分区):
 * 热消息：登录后的消息
 * 冷消息: 登录前的消息
 * 每个会话的冷热消息分别用一个key进行存储，当有新的消息时，只更新热消息key，冷消息key保持不变
 * 由于热消息数量不会很大，所以localForage.setItem耗时很低，可以防止消息过多时出现卡顿的情况
 *
 * 优化2(延迟渲染):
 * 拉取消息时，如果直接用state.chats接收，页面就开始渲染，一边渲染页面一边大量接消息会导致很严重的卡顿
 * 为了加速拉取离线消息效率，拉取时消息暂时存储到cacheChats,等待所有离线消息拉取完成后，再统一放至state中进行渲染
 * 
 * 优化3(pinia代替vuex)
 * 实测pinia的远超vuex,且语法更简洁清晰
 * 
 * */

// === 存储优化：冷热分离 Key Scheme ===
const MAX_HOT_MSGS = 200;

// 性能优化：冻结消息对象，Vue 2 遇到 Object.isFrozen() 会跳过响应式代理
function freezeMessages(msgs) {
	if (!msgs) return;
	for (let i = 0; i < msgs.length; i++) {
		if (msgs[i] && !Object.isFrozen(msgs[i])) {
			Object.freeze(msgs[i]);
		}
	}
}
const COLD_PAGE_SIZE = 100;
const MAX_TOTAL_MSGS = 5000;
const MAX_CHAT_MSGS = 500;

function buildKey(userId, adminId, name) {
  return `chats-${userId}-${adminId}-${name}`;
}
function metaKey(base, convID)    { return `${base}-${convID}-meta`; }
function hotKey(base, convID)     { return `${base}-${convID}-hot`; }
function coldIdxKey(base, convID) { return `${base}-${convID}-cold-index`; }
function coldPageKey(base, convID, page) { return `${base}-${convID}-cold-${page}`; }

// 从 chat 对象提取纯元数据（排除 messages、临时状态等大字段）
function extractMeta(chat) {
  return {
    conversationID: chat.conversationID, tenantID: chat.tenantID,
    convType: chat.convType, scene: chat.scene,
    userID: chat.userID, showName: chat.showName, faceURL: chat.faceURL,
    remark: chat.remark, status: chat.status,
    corpUserID: chat.corpUserID, personalUserID: chat.personalUserID,
    relationStatus: chat.relationStatus, groupID: chat.groupID,
    groupMemberCount: chat.groupMemberCount || 0,
    identity: chat.identity, source: chat.source, customer: chat.customer,
    groupLabelID: chat.groupLabelID, isPinned: chat.isPinned, takeOver: chat.takeOver,
    latestMsg: chat.latestMsg, latestMsgSendTime: chat.latestMsgSendTime,
    latestMsgType: chat.latestMsgType, unreadCount: chat.unreadCount,
    createdAt: chat.createdAt, updatedAt: chat.updatedAt,
    atMe: chat.atMe, atAll: chat.atAll, isDnd: chat.isDnd,
    hotCount: Array.isArray(chat.messages) ? Math.min(chat.messages.length, MAX_HOT_MSGS) : 0,
  };
}

let cacheChats = [];

export default defineStore('chatStore', {
	state: () => {
		return {
			activeChat: null, // 当前激活的会话框
			chatLabels: [],   // 会话标签列表
			chats: [],        // 会话列表数据
			chatIndexMap: {}, // { conversationID: index } O(1) 查找索引
			privateMsgMaxId: 0,
			groupMsgMaxId: 0,
			loading: false,   // 加载消息状态（数据面翻转中：cacheChats 影子数组查找/写入、索引与落盘守卫）
			silentLoading: false, // silent 周期同步标记：UI 层（Chat 页占位/缓存重建）据此跳过本次 loading 切换
			quickReplies: [], // 快捷回复
			autoReplyScripts: [], // 自动回复话术
			followUpConfig: null, // 超时跟进配置（每个客服一份）
			dirtyTracker: {}, // { convID: Set<"meta"|"hot"> } 脏数据追踪
			coldPageInFlight: {}, // { convID: Set<pageIndex> } 冷页加载去重
			schemaVersion: 2, // 存储格式版本
			// 性能优化：rAF 批量合并排序标记
			_resortDirty: false,
			_resortPending: false,
			// 性能优化：分组聚合缓存（增量维护，避免 computed 全量遍历）
			_groupSummary: {},
		}
	},
	actions: {
		// 初始化本地会话列表
		initChats(chatsData) {
			this.chats = [];
			this.privateMsgMaxId = chatsData.privateMsgMaxId || 0;
			this.groupMsgMaxId = chatsData.groupMsgMaxId || 0;
			cacheChats = (chatsData.chats || []).map(chat => {
				if (!Array.isArray(chat.messages)) {
					chat.messages = [];
				}
				return chat;
			});
		},
		// 快捷回复列表
		setQuickReplies(replies) {
			this.quickReplies = replies || [];
		},
		// 增加快捷回复
		addQuickReply(reply){
			this.quickReplies.push(reply);
		},
		// 更新快捷回复
		updateQuickReply(updatedReply) {
			const index = this.quickReplies.findIndex(reply => reply.id === updatedReply.id);
			if (index !== -1) {
				this.quickReplies.splice(index, 1, updatedReply);
			}
		},
		// 删除快捷回复
		deleteQuickReply(id) {
			this.quickReplies = this.quickReplies.filter(reply => reply.id !== id);
		},
		// 自动回复话术列表
		setAutoReplyScripts(scripts) {
			this.autoReplyScripts = scripts || []
		},
		addAutoReplyScript(script) {
			this.autoReplyScripts.push(script)
		},
		updateAutoReplyScript(updatedScript) {
			const index = this.autoReplyScripts.findIndex(s => s.id === updatedScript.id)
			if (index !== -1) {
				Object.assign(this.autoReplyScripts[index], updatedScript)
			}
		},
		deleteAutoReplyScript(id) {
			this.autoReplyScripts = this.autoReplyScripts.filter(s => s.id !== id)
		},
		// 超时跟进配置（每个客服一份）
		setFollowUpConfig(config) {
			this.followUpConfig = config || null
		},
		// 会话分组标签初始化
		setChatLables(labels) {
			this.chatLabels = labels || [];
		},
		// 增加标签
		addChatLabel(label) {
			this.chatLabels.push(label);
		},
		// 删除标签
		deleteChatLabel(label) {
			this.chatLabels = this.chatLabels.filter(labelElem => labelElem.id !== label.id)
			let flag = false;
			let chats = this.findChats();
			for (let idx in chats) {
				if (chats[idx].groupLabelID === label.id) {
					flag = true;
					const oldGid = effectiveGroupID(chats[idx]);
					const unread = chats[idx].unreadCount || 0;
					chats[idx].groupLabelID = 0;
					const newGid = effectiveGroupID(chats[idx]);
					if (oldGid !== newGid) {
						if (this._groupSummary[oldGid]) {
							this._groupSummary[oldGid].chatCount = Math.max(0, (this._groupSummary[oldGid].chatCount || 0) - 1);
							this._groupSummary[oldGid].unreadCount = Math.max(0, (this._groupSummary[oldGid].unreadCount || 0) - unread);
						}
						if (!this._groupSummary[newGid]) {
							this._groupSummary[newGid] = { chatCount: 0, unreadCount: 0 };
						}
						this._groupSummary[newGid].chatCount = (this._groupSummary[newGid].chatCount || 0) + 1;
						this._groupSummary[newGid].unreadCount = (this._groupSummary[newGid].unreadCount || 0) + unread;
					}
					chats[idx].stored = false;
				}
			}
			if (flag){
				this.saveToStorage();
			}
		},
		// 同步服务器会话
		syncChats(reConnect,dbChats) {
			// 确保 cacheChats 是一个数组，如果不是则初始化为空数组
			if (!Array.isArray(cacheChats)) {
				cacheChats = [];
			}

			// 处理断线重连的情况 (此时cacheChats是空的,对比这个会拉取所有消息，耗时)
			let merged = [];
			if (reConnect) {
				// 重连时优先使用 this.chats；若 refreshChats 尚未将 cacheChats 合并进来，
				// 则回退到 cacheChats，避免所有服务端会话被误判为"新会话"全量预拉取
				const base = (this.chats && this.chats.length > 0) ? this.chats : (cacheChats || []);
				merged = [...base];
			} else {
				// 非重连：优先用 cacheChats（初始加载由 loadChat 填充的本地基准）；
				// 若 cacheChats 已被上轮 refreshChats 消费（null/空，如 10s 连线超时兜底
				// 先跑完后 WS 迟到连上、visibilitychange 直连接管等场景再次触发同步），
				// 必须回退 this.chats——否则 merged 从空数组起步，全部服务端会话被误判为
				// "新会话"，refreshChats 时用服务端快照对象整体替换 this.chats，
				// 导致打开中会话的消息/未读/已读链路与新对象分裂（表现为收不到新消息）
				const base = (Array.isArray(cacheChats) && cacheChats.length > 0) ? cacheChats : (this.chats || []);
				merged = [...base]; // 深拷贝本地数组，避免直接修改原数据
			}
			

			// 需要拉取消息的会话数组
			const sessionsNeedUpdate = [];
			// O(1) Map lookup instead of O(n) findIndex for tens of thousands of conversations
			const mergedMap = new Map(merged.map(c => [c.conversationID, c]));

			// Walk each conversation from the server
			dbChats.forEach(dbConv => {
				// Check if the conversation already exists locally
				const localConv = mergedMap.get(dbConv.conversationID);
				if (localConv) {
					// 本地待落库标记：服务端已返回该会话（首条消息已落库）→ 清除
					if (localConv._localOnly) {
						localConv._localOnly = false;
					}
					const dbLatestTime = dbConv.latestMsgSendTime || 0;
					const localLatestTime = localConv.latestMsgSendTime || 0;

					// Snapshot pre-sync state for incremental _groupSummary update
					const oldRelStatus = localConv.relationStatus;
					const oldGroupLabelID = localConv.groupLabelID;

					// relationStatus sync: trust server on first load; on reconnect only when newer message or normal status
					// 【已失效】Prevents WS FRIEND_DELETED_TYPE/FRIEND_BLACK_TYPE from being overwritten by server staled data
					// relationStatus 现仅作字段留痕、不再驱动分组（自动归组已移除），本守卫仅保留字段同步策略
					if (!reConnect || dbLatestTime > localLatestTime || oldRelStatus === 0) {
						localConv.relationStatus = dbConv.relationStatus;
					}

					// 先取本地最后一条消息 id（同秒缺口补拉需要精确双游标）
					let lastMsgID = 0;
					if (Array.isArray(localConv.messages) && localConv.messages.length > 0) {
						lastMsgID = localConv.messages[localConv.messages.length - 1].id || 0;
					}

					// Determine if newer messages exist
					// 时间严格更大 → 普通增量；时间相等但本地有消息（lastMsgID>0）→
					// 同秒缺口（如批量导入/WS 丢失的同秒消息），用 (send_time, id) 双游标精确补拉。
					// lastMsgID>0 条件避免 meta-only 会话（messages 为空）每次同步都进更新列表。
					// 已验证游标匹配（上次增量查询确认该游标处无缺口）时跳过同秒探测：
					// 否则稳态（本地与服务器已同步）每轮周期同步都会对全部会话发起无收益增量查询
					const hasNewer = dbLatestTime > localLatestTime;
					const vc = localConv._verifiedCursor;
					const cursorMatch = !!vc && vc.time === dbLatestTime && vc.id === lastMsgID;
					const sameSecondGap = dbLatestTime === localLatestTime && lastMsgID > 0 && !cursorMatch;
					if (hasNewer || sameSecondGap) {
						// Server has newer messages, need to fetch
						sessionsNeedUpdate.push({
							conversationID: dbConv.conversationID,
							lastMessageTime: localLatestTime,
							syncType: 'incremental',
							lastMsgID: lastMsgID,
						});

						// Update local conversation info with fields the server returns
						localConv.conversationID = dbConv.conversationID;
						localConv.showName= dbConv.showName;
						localConv.faceURL = dbConv.faceURL;
						localConv.unreadCount = Math.max(dbConv.unreadCount || 0, localConv.unreadCount || 0);
						localConv.latestMsg = dbConv.latestMsg;
						localConv.latestMsgSendTime = dbConv.latestMsgSendTime;
						localConv.latestMsgType = dbConv.latestMsgType;
						localConv.isPinned= dbConv.isPinned;
						localConv.status= dbConv.status;
						localConv.remark= dbConv.remark;
						localConv.identity= dbConv.identity;
						localConv.source = dbConv.source;
						localConv.groupLabelID = dbConv.groupLabelID;
						localConv.customer = dbConv.customer;
						localConv.convType = dbConv.convType;
						localConv.scene = dbConv.scene;
						localConv.corpUserID = dbConv.corpUserID;
						localConv.personalUserID = dbConv.personalUserID;
						localConv.groupID = dbConv.groupID;
						localConv.createdAt = dbConv.createdAt;
						localConv.updatedAt = dbConv.updatedAt;
					}
					// 群人数无条件同步：服务端群成员变动回调可能更新 group_member_count，
					// 若只在有新消息时同步，安静群（此后无消息）的人数会长期显示旧值
					if ((dbConv.groupMemberCount || 0) !== (localConv.groupMemberCount || 0)) {
						localConv.groupMemberCount = dbConv.groupMemberCount || 0;
					}

					// 免打扰状态同步：多端传播；本地刚切换（10s 内）时短路，
					// 避免会话列表 GET 快照早于 POST 提交、但响应晚到时的开关回弹
					// （relationStatus 同步已有同款 guard 先例）
					if (!localConv._dndAt || Date.now() - localConv._dndAt > 10000) {
						localConv.isDnd = dbConv.isDnd;
					}

					// Incrementally update _groupSummary after all field syncs
					if (!localConv.delete) {
						const oldGid = effectiveGroupID({ ...localConv, /* relationStatus: oldRelStatus, */ groupLabelID: oldGroupLabelID });
						const newGid = effectiveGroupID(localConv);
						if (oldGid !== newGid) {
							const unread = localConv.unreadCount || 0;
							if (this._groupSummary[oldGid]) {
								this._groupSummary[oldGid].chatCount = Math.max(0, (this._groupSummary[oldGid].chatCount || 0) - 1);
								this._groupSummary[oldGid].unreadCount = Math.max(0, (this._groupSummary[oldGid].unreadCount || 0) - unread);
							}
							if (!this._groupSummary[newGid]) {
								this._groupSummary[newGid] = { chatCount: 0, unreadCount: 0 };
							}
							this._groupSummary[newGid].chatCount = (this._groupSummary[newGid].chatCount || 0) + 1;
							this._groupSummary[newGid].unreadCount = (this._groupSummary[newGid].unreadCount || 0) + unread;
						}
					}
				} else {
					// New conversation: sync metadata and fetch recent messages
					if (!Array.isArray(dbConv.messages)) {
						dbConv.messages = [];
					}
					merged.push(dbConv);
					dbConv.stored = false;
					sessionsNeedUpdate.push({
						conversationID: dbConv.conversationID,
						lastMessageTime: 0,
						syncType: 'initial',
						lastMsgID: 0,
					});
				}
			});

			// Cap new-conversation preloading to avoid O(N) HTTP + UNION ALL
			// Only the most recently active new conversations get message preloading;
			// the rest load on demand when the user clicks into them.
			// Applies on both first load and reconnect as a safety limit.
			const MAX_NEW_CONV_SYNC = 200;
			{
				const initialItems = [];
				const incrementalItems = [];
				for (const item of sessionsNeedUpdate) {
					if (item.syncType === 'initial') {
						initialItems.push(item);
					} else {
						incrementalItems.push(item);
					}
				}
				if (initialItems.length > MAX_NEW_CONV_SYNC) {
					// Sort by latestMsgSendTime desc and keep only the N most recently active
					const timeMap = new Map();
					for (const dbConv of dbChats) {
						timeMap.set(dbConv.conversationID, dbConv.latestMsgSendTime || 0);
					}
					initialItems.sort((a, b) => (timeMap.get(b.conversationID) || 0) - (timeMap.get(a.conversationID) || 0));
					const kept = initialItems.slice(0, MAX_NEW_CONV_SYNC);
					console.log(`[chatStore] Capping new-conv sync: ${initialItems.length} → ${MAX_NEW_CONV_SYNC} (reConnect=${reConnect})`);
					// Rebuild sessionsNeedUpdate: keep all incremental + capped initial items
					sessionsNeedUpdate.length = 0;
					for (const item of incrementalItems) {
						sessionsNeedUpdate.push(item);
					}
					for (const item of kept) {
						sessionsNeedUpdate.push(item);
					}
				}
			}

			// Write back to module-level cache
			// 清理本地有但服务端列表不存在的会话（已归档/删除）：
			// 服务端 status=0 全量列表是权威，不在列表即已归档/删除。本地副本若一直保留，
			// IndexedDB 缓存会无限累积（kf_004 曾 9114 条，loadChat 每次刷新主线程阻塞 40s+，
			// 页面卡死"消息接收中..."）。归档会话被新消息唤醒时服务端列表重新返回，
			// 按新会话（initial）重新拉取，不丢数据。
			// 保留项：用户主动删除的（delete=true）走既有保存流程清理；
			// stored=false（有未落盘变更，如刚创建/刚更新的会话）可能暂未出现在服务端列表，
			// 清理会丢失本地未保存状态，同样保留。
			if (Array.isArray(dbChats) && dbChats.length > 0) {
				const serverIDs = new Set(dbChats.map(c => c.conversationID));
				const ghost = [];
				merged = merged.filter(c => {
					if (!c || c.delete || c.stored === false) return true;
					if (serverIDs.has(c.conversationID)) return true;
					// 本地主动发起、服务端尚未落库的新会话（_localOnly）：不视为幽灵，
					// 否则发起后未发首条消息即被本次周期同步清理，导致后续发送找不到会话而失败。
					// 注意：该标记不持久化（extractMeta 白名单外），仅本次页面会话内豁免；
					// 刷新后未落库的空会话会被周期同步清理（与“发送第一条消息后会话会保存”口径一致），
					// 首条消息落库后服务端列表返回该会话，由命中分支清除标记
					if (c._localOnly) return true;
					ghost.push(c);
					return false;
				});
				if (ghost.length > 0) {
					console.warn(`[chatStore] 清理本地归档/删除会话 ${ghost.length} 个（服务端列表不存在）`);
					// 防写回：防止 refreshChats 后的保存把已清理会话重新写回 IndexedDB
					ghost.forEach(c => {
						if (c && c.conversationID) {
							delete this.dirtyTracker[c.conversationID];
							c.stored = true;
							c._ghostCleaned = true;
						}
					});
					// 异步批量清理 IndexedDB 残留（meta/hot/冷页/索引），不阻塞同步流程
					const userStore = useUserStore();
					const base = buildKey(userStore.userInfo.id, userStore.userInfo.adminID, userStore.userInfo.name);
					const ghostIDs = ghost.map(c => c.conversationID).filter(Boolean);
					setTimeout(() => {
						if (ghostIDs.length > 0) {
							this._cleanupGhostConversations(base, ghostIDs);
						}
					}, 0);
				}
			}
			cacheChats = merged;

			return sessionsNeedUpdate;
		},
		// 同步服务器消息
		addMessagesToConversation(conversationID, newMessages) {
			// 与 findChats getter 保持一致：loading 期间用 cacheChats，否则用 this.chats
			let chats;
			if (cacheChats && this.loading) {
				chats = cacheChats;
			} else {
				chats = this.chats;
			}
			if (!Array.isArray(chats)) {
				chats = [];
			}
			const conversation = chats.find(chat => chat.conversationID === conversationID);
			if (conversation) {
				// 确保 messages 数组存在
				if (!Array.isArray(conversation.messages)) {
					conversation.messages = [];
				}
				
				// 去重处理
				const existingMessageIds = new Set(conversation.messages.map(m => m.id));
				const uniqueNewMessages = newMessages.filter(msg => !existingMessageIds.has(msg.id));

				// 确保每条消息有稳定 key（虚拟滚动需要）
				uniqueNewMessages.forEach(msg => {
					if (!msg.id && !msg.tmpID && !msg.tmpId && !msg._key) {
						msg._key = 'k_' + Date.now() + '_' + Math.random().toString(36).slice(2);
					}
				});

				if (uniqueNewMessages.length > 0) {
					// 归并两个已排序数组（O(n+m) 替代 O((n+m)log(n+m)) 全量排序）
					conversation.messages = mergeSortedMessages(conversation.messages, uniqueNewMessages);
					
					// 更新会话预览文本和最新消息时间
					const lastNew = uniqueNewMessages[uniqueNewMessages.length - 1];
					// 仅当新批次不早于当前最新时更新会话预览，
					// 避免增量缺口 older 翻页合并旧批次时 latestMsgSendTime 回退，
					// 否则会引发会话排序错乱与下次同步重复拉取（hasNewer 反复为 true）
					if ((lastNew.sendTime || 0) >= (conversation.latestMsgSendTime || 0)) {
						conversation.latestMsg = latestMsgPreview(lastNew.msgType, lastNew.content);
						conversation.latestMsgSendTime = lastNew.sendTime;
					}
					
					// 更新未读计数：仅实时消息（单条）计入，批量加载不重加历史
					if (uniqueNewMessages.length === 1) {
						uniqueNewMessages.forEach(msg => {
							if (!msg.isWX && !conversation.isDnd && conversation !== this.activeChat) {
								conversation.unreadCount += 1;
								const gid = effectiveGroupID(conversation);
								if (!this._groupSummary[gid]) {
									this._groupSummary[gid] = { chatCount: 0, unreadCount: 0 };
								}
								this._groupSummary[gid].unreadCount = (this._groupSummary[gid].unreadCount || 0) + 1;
							}
						});
					}
					
					// 保证存入存储中
					conversation.stored = false;
					
				}
			}
		},
		// 主动增量拉取单个会话的新消息（复用 syncChats 的游标语义与批量接口）：
		// 供转发等"后台发送"成功后调用，让目标会话的消息即时可见，无需等周期同步/刷新页面
		async refreshConversationMessages(conversationID) {
			const chat = (this.chats || []).find(c => c && c.conversationID === conversationID);
			if (!chat) return; // 会话不在本地列表（未加载过），打开时会话自然拉取，无需处理
			let lastMsgID = 0;
			if (Array.isArray(chat.messages) && chat.messages.length > 0) {
				lastMsgID = chat.messages[chat.messages.length - 1].id || 0;
			}
			try {
				const data = await getMessagesBatch([{
					conversationID: conversationID,
					lastMessageTime: chat.latestMsgSendTime || 0,
					syncType: 'incremental',
					lastMsgID: lastMsgID,
				}]);
				if (data && data.results && data.results[conversationID] && data.results[conversationID].length > 0) {
					this.addMessagesToConversation(conversationID, data.results[conversationID]);
					this.markSyncVerified(conversationID);
				}
			} catch (e) {
				// 拉取失败不打扰用户：周期同步/刷新兜底，不影响转发本身已成功
				console.warn('[chatStore] 主动拉取会话消息失败:', conversationID, e);
			}
		},
		// 增量查询完成后标记已验证游标（最新消息时间+最后消息 id）：
		// 下次 syncChats 同秒缺口探测游标匹配时跳过，避免稳态重复查询；
		// 新消息到达（WS/发送/增量合入）会改变最后消息 id，游标自然失效重新探测
		markSyncVerified(conversationID) {
			let chats = this.findChats();
			const chat = chats.find(c => c.conversationID === conversationID);
			if (!chat || !Array.isArray(chat.messages) || chat.messages.length === 0) return;
			const last = chat.messages[chat.messages.length - 1];
			// 最后一条 id=0 为未回填的临时消息：此时 syncChats 的 lastMsgID=0 探测
			// 条件本身关闭，写游标无意义且会在回填后残留旧值，直接跳过等下次查询
			if (!last.id) return;
			chat._verifiedCursor = {
				time: chat.latestMsgSendTime || last.sendTime || 0,
				id: last.id,
			};
		},
		openChat(chatInfo) {
			let chats = this.findChats();
			let chat = null;
			for (let idx in chats) {
				if (chats[idx].conversationID === chatInfo.conversationID) {
					chat = chats[idx];
					// 曾被删除的会话收到新消息时恢复
					if (chat.delete) {
						chat.delete = false;
						// 恢复 _groupSummary 计数
						const gid = effectiveGroupID(chat);
						if (!this._groupSummary[gid]) {
							this._groupSummary[gid] = { chatCount: 0, unreadCount: 0 };
						}
						this._groupSummary[gid].chatCount = (this._groupSummary[gid].chatCount || 0) + 1;
						this._groupSummary[gid].unreadCount = (this._groupSummary[gid].unreadCount || 0) + (chat.unreadCount || 0);
					}
					// 合并身份字段，用新值填补空位
					if (!chat.identity && chatInfo.identity) chat.identity = chatInfo.identity;
					if (!chat.corpUserID && chatInfo.corpUserID) chat.corpUserID = chatInfo.corpUserID;
					if (!chat.personalUserID && chatInfo.personalUserID) chat.personalUserID = chatInfo.personalUserID;
					// 再次发起时更新选定发信号（internal 会话多号场景）。
					// Vue2 响应式：旧会话对象（IndexedDB/服务端装载）可能无该字段，
					// 必须用 Vue.set 才能触发 ChatBox 的 chat.sendAccountVID 监听
					if (chatInfo.sendAccountVID) {
						if (chat.sendAccountVID !== undefined) {
							chat.sendAccountVID = chatInfo.sendAccountVID;
						} else {
							Vue.set(chat, 'sendAccountVID', chatInfo.sendAccountVID);
						}
					}
					// 放置头部
					this.moveTop(idx);
					break;
				}
			}
			// 创建会话
			if (chat == null) {
				const userStore = useUserStore();
				chat = {
					// 基础标识
					conversationID: chatInfo.conversationID,
					tenantID: chatInfo.tenantID || 1,
					convType: chatInfo.convType || 'single',  // 新增：会话类型
					scene: chatInfo.scene || 'customer_service',  // 新增：场景
					
					// 显示信息
					userID: chatInfo.userID,
					showName: chatInfo.showName,
					faceURL: chatInfo.faceURL,
					remark: chatInfo.remark || '',
					status: chatInfo.status || 0,
					
					// 单聊特有字段（新增）
					corpUserID: chatInfo.corpUserID || extractParticipants(chatInfo.conversationID).corpID,
					personalUserID: chatInfo.personalUserID || extractParticipants(chatInfo.conversationID).personalID,
					relationStatus: chatInfo.relationStatus || 0,
					
					// 群聊特有字段
					groupID: chatInfo.groupID || '',
					
					// 业务归属
					identity: chatInfo.identity || '',
					source: chatInfo.source || '',
					customer: userStore.userInfo.name,
					groupLabelID: chatInfo.groupLabelID || 0,  // 修改：从 groupLabel 改为 groupLabelID
					isPinned: chatInfo.isPinned || false,
					takeOver: chatInfo.takeOver || 0,  // 新增：接管状态
					
					// 消息快照
					latestMsg: chatInfo.latestMsg || '',
					latestMsgSendTime: chatInfo.latestMsgSendTime || Math.floor(Date.now() / 1000),
					latestMsgType: chatInfo.latestMsgType || 0,  // 新增：最新消息类型
					unreadCount: 0,
					
					// 时间戳
					createdAt: chatInfo.createdAt || Math.floor(Date.now() / 1000),
					updatedAt: chatInfo.updatedAt || Math.floor(Date.now() / 1000),
					
					// 本地字段
					messages: [],
					readedMessageIdx: 0,
					hotMinIdx: 0,
					atMe: false,
					atAll: false,
					stored: false,
					delete: false,
					// internal（企微对企微）发起会话：选定发信号（随发送链路账户透传）
					sendAccountVID: chatInfo.sendAccountVID || '',
					// 本地主动发起、服务端尚未落库的新会话标记：
					// 周期同步的幽灵过滤豁免（首条消息落库后由 syncChats 命中分支清除）
					_localOnly: !!chatInfo._localOnly
				};
				chats.unshift(chat);
				if (!this.loading) {
					this._shiftIndexFrom(0, 1);
					this.chatIndexMap[chat.conversationID] = 0;
				}
				// 性能优化：增量更新分组汇总
				const ogid = effectiveGroupID(chat);
				if (!this._groupSummary[ogid]) {
					this._groupSummary[ogid] = { chatCount: 0, unreadCount: 0 };
				}
				this._groupSummary[ogid].chatCount = (this._groupSummary[ogid].chatCount || 0) + 1;
				this.saveToStorage('meta', chat.conversationID);
			}
		},
		// 设置当前会话框
		setActiveChat(idx) {
			let chats = this.findChats();
			let chat = chats[idx];
			if (chat && !Array.isArray(chat.messages)) {
				chat.messages = [];
			}

			if (this.activeChat && this.activeChat._lruTimer) {
				clearTimeout(this.activeChat._lruTimer);
				this.activeChat._lruTimer = null;
			}
			if (chat && chat._lruTimer) {
				clearTimeout(chat._lruTimer);
				chat._lruTimer = null;
			}

			const prevChat = this.activeChat;
			if (prevChat && prevChat !== chat && Array.isArray(prevChat.messages) && prevChat.messages.length > MAX_HOT_MSGS) {
				prevChat._lruTimer = setTimeout(() => {
					if (this.activeChat !== prevChat && Array.isArray(prevChat.messages)) {
						// LRU 裁剪必须与 _archivedCount 不变量对齐：只裁「已在冷页有副本」的头部消息，
						// 未归档消息保留（随后续保存归档），否则 filterMessage 会按虚高的
						// _archivedCount 裁掉未归档消息，形成本地永久缺口
						const dropped = prevChat.messages.length - MAX_HOT_MSGS;
						const safeDropped = Math.min(dropped, prevChat._archivedCount || 0);
						if (safeDropped > 0) {
							prevChat.messages = prevChat.messages.slice(safeDropped);
							prevChat._archivedCount = (prevChat._archivedCount || 0) - safeDropped;
						}
						// 裁剪量未达到 dropped（头部含未归档消息）：强制标记脏并触发保存，
						// 让 _archiveColdPage 立即归档未归档段；否则该会话无后续改动时
						// 未归档消息仅存内存，刷新页面即丢失
						if (safeDropped < dropped) {
							prevChat.stored = false;
							this.saveToStorage('hot', prevChat.conversationID);
						}
						prevChat._lruTimer = null;
					}
				}, 30000);
			}

			this.activeChat = chat;
		},
		// 备注
		remarkChat(chatInfo) {
			let chats = this.findChats();
			for (let idx in chats) {
				if (chats[idx].conversationID == chatInfo.conversationID) {
					chats[idx].remark = chatInfo.remark;
					chats[idx].stored = false;
					this.saveToStorage();
					break;
				}
			}
		},
		// 分组
		markChatToGroup(chatInfo) {
			let chats = this.findChats();
			for (let idx in chats) {
				if (chats[idx].conversationID == chatInfo.conversationID) {
					// 性能优化：增量更新 _groupSummary
					const oldGid = effectiveGroupID(chats[idx]);
					chats[idx].groupLabelID = chatInfo.groupLabelID;
					const newGid = effectiveGroupID(chats[idx]);
					if (oldGid !== newGid) {
						const unread = chats[idx].unreadCount || 0;
						if (this._groupSummary[oldGid]) {
							this._groupSummary[oldGid].chatCount = Math.max(0, (this._groupSummary[oldGid].chatCount || 0) - 1);
							this._groupSummary[oldGid].unreadCount = Math.max(0, (this._groupSummary[oldGid].unreadCount || 0) - unread);
						}
						if (!this._groupSummary[newGid]) {
							this._groupSummary[newGid] = { chatCount: 0, unreadCount: 0 };
						}
						this._groupSummary[newGid].chatCount = (this._groupSummary[newGid].chatCount || 0) + 1;
						this._groupSummary[newGid].unreadCount = (this._groupSummary[newGid].unreadCount || 0) + unread;
					}
					chats[idx].stored = false;
					this.saveToStorage();
					break;
				}
			}
		},
		// 重设未读数
		resetUnreadCount(chatInfo) {
			let chats = this.findChats();
			for (let idx in chats) {
				if (chats[idx].conversationID == chatInfo.conversationID) {
					// 性能优化：增量更新分组汇总
				const gid = effectiveGroupID(chats[idx]);
				const oldUnread = chats[idx].unreadCount || 0;
				if (this._groupSummary[gid] && oldUnread > 0) {
					this._groupSummary[gid].unreadCount = Math.max(0,
						(this._groupSummary[gid].unreadCount || 0) - oldUnread);
				}
				chats[idx].unreadCount = 0;
					chats[idx].atMe = false;
					chats[idx].atAll = false;
					chats[idx].stored = false;
					// 立即持久化未读数归零，绕过 debounce，防止刷新时丢数据
					// 先读取现有 meta 再合并写入，避免覆盖丢失 showName/faceURL 等所有其他字段
					const userStore = useUserStore();
					const base = buildKey(userStore.userInfo.id, userStore.userInfo.adminID, userStore.userInfo.name);
					if (userStore.userInfo.id && userStore.userInfo.name) {
						const metaName = metaKey(base, chats[idx].conversationID);
						localForage.getItem(metaName).then(existing => {
							const merged = { ...(existing || {}), unreadCount: 0, atMe: false, atAll: false };
							return localForage.setItem(metaName, merged);
						}).catch(() => {});
					}
					this.saveToStorage();
					break;
				}
			}
		},
		//  已读消息
		readedMessage(pos) {
			let chat = this.findChatByFriend(pos.friendId);
			if (!chat || !Array.isArray(chat.messages)) return;
			for (let idx = chat.readedMessageIdx || 0; idx < chat.messages.length; idx++) {
				let m = chat.messages[idx];
				if (m.id && m.isWX && m.status < MESSAGE_STATUS.RECALL) {
					// pos.maxId为空表示整个会话已读
					if (!pos.maxId || m.id <= pos.maxId) {
						m.status = MESSAGE_STATUS.READED
						chat.readedMessageIdx = idx;
						chat.stored = false;
					}
				}
			}
			this.saveToStorage('hot', chat.conversationID);
		},
		// 新已读消息
		newReadedMessage(msgInfo) {
			let chat = this.findChat({"conversationID": msgInfo.conversationID});
			if (!chat || !Array.isArray(chat.messages)) return;
			for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
				let m = chat.messages[idx];
				if (m.id && m.isWX && m.id <= msgInfo.id && m.status < MESSAGE_STATUS.RECALL) {
					m.status = MESSAGE_STATUS.READED
					chat.stored = false;
				}
			}
			this.saveToStorage('hot', msgInfo.conversationID);
		},
		// 消息发送失败（后端 WS 推送 cmd=12）
		markMessageFailed(payload) {
			let chat = this.findChat({"conversationID": payload.conversationID});
			if (!chat || !Array.isArray(chat.messages)) return;
			for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
				let m = chat.messages[idx];
				if (m.syncKey && m.syncKey === payload.syncKey) {
					m.status = MESSAGE_STATUS.FAILED;
					chat.stored = false;
					this.saveToStorage('hot', payload.conversationID);
					return;
				}
			}
		},
		// 移除会话
		removeChat(idx) {
			let chats = this.findChats();
			if (chats[idx] == this.activeChat) {
				this.activeChat = null;
			}
			chats[idx].delete = true;
			// 性能优化：增量更新分组汇总
			const rgid = effectiveGroupID(chats[idx]);
			if (this._groupSummary[rgid]) {
				this._groupSummary[rgid].chatCount = Math.max(0,
					(this._groupSummary[rgid].chatCount || 0) - 1);
				this._groupSummary[rgid].unreadCount = Math.max(0,
					(this._groupSummary[rgid].unreadCount || 0) - (chats[idx].unreadCount || 0));
			}
			chats[idx].stored = false;
			this.saveToStorage();
		},
		// 移除私聊
		removePrivateChat(friendId) {
			let chats = this.findChats();
			for (let idx in chats) {
				if (chats[idx].personalUserID == friendId) {
					this.removeChat(idx);
					break;
				}
			}
		},
		// 移除群聊
		removeGroupChat(groupId) {
			let chats = this.findChats();
			for (let idx in chats) {
				if (chats[idx].groupID == groupId) {
					this.removeChat(idx);
					break;
				}
			}
		},
		// 会话移动到顶部
		moveTop(idx) {
			// 加载中不移动，很耗性能
			if (this.loading) {
				return;
			}
			if (idx > 0) {
				let chats = this.findChats();
				let chat = chats[idx];
				const convID = chat.conversationID;
				chats.splice(idx, 1);
				chats.unshift(chat);
				// 更新索引: 0..idx-1 全部 +1, 被移动的设为 0
				if (this.chatIndexMap[convID] !== undefined) {
					for (const cid in this.chatIndexMap) {
						if (this.chatIndexMap[cid] < idx) this.chatIndexMap[cid] += 1;
					}
					this.chatIndexMap[convID] = 0;
				}
				chat.stored = false;
				this.saveToStorage('meta', chat.conversationID);
			}
		},
		// 设置置顶
		setPinned (idx) {
			if (this.loading) {
				return;
			}
			let chats = this.findChats();
			let chat = chats[idx];
			chat.isPinned = chat.isPinned? false:true;
			chat.stored = false;
			this.saveToStorage('meta', chat.conversationID);
		},
		// 插入消息
		insertMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			// 防脏索引（会话已被清理/索引失效）：直接返回，避免 undefined 索引抛异常中断发送链路
			if (!chat) {
				return;
			}
			if (!Array.isArray(chat.messages)) {
				chat.messages = [];
			}
			// 会话收到新消息即恢复有效：复位幽灵清理标记，允许后续正常写回 IndexedDB
			if (chat._ghostCleaned) {
				chat._ghostCleaned = false;
			}
			// 去重：WS 实时消息可能与增量拉取结果重复（同一消息双通道到达）
			if (msgInfo.id || msgInfo.tmpID || msgInfo.tmpId) {
				const dup = chat.messages.find(m => {
					if (msgInfo.id && m.id && m.id == msgInfo.id) return true;
					if (!msgInfo.id && msgInfo.tmpID && m.tmpID && m.tmpID == msgInfo.tmpID) return true;
					if (!msgInfo.id && msgInfo.tmpId && m.tmpId && m.tmpId == msgInfo.tmpId) return true;
					return false;
				});
				if (dup) {
					// 同 id 消息已存在：撤回等状态同步（服务端广播更新后的消息对象）在此更新本地状态
					if (msgInfo.status !== undefined && msgInfo.status >= MESSAGE_STATUS.RECALL && dup.status < MESSAGE_STATUS.RECALL) {
						dup.status = msgInfo.status;
						chat.stored = false;
					}
					return;  // 已存在，忽略重复插入
				}
			}
			// 插入新的数据（使用已导入的 MESSAGE_TYPE 常量）
			// 仅当新消息不早于当前最新时更新会话预览，避免乱序消息回退时间
			if ((msgInfo.sendTime || 0) >= (chat.latestMsgSendTime || 0)) {
				chat.latestMsg = latestMsgPreview(msgInfo.msgType, msgInfo.content);
				chat.latestMsgSendTime = msgInfo.sendTime;
			}
			if (!msgInfo.isWX && !chat.isDnd && chat !== this.activeChat){ // 对方发送且非活跃会话且未屏蔽才未读+1
				chat.unreadCount += 1;
				// 性能优化：增量更新分组汇总，替代 computed 全量遍历
				const gid = effectiveGroupID(chat);
				if (!this._groupSummary[gid]) {
					this._groupSummary[gid] = { chatCount: 0, unreadCount: 0 };
				}
				this._groupSummary[gid].unreadCount = (this._groupSummary[gid].unreadCount || 0) + 1;
			}
			// 确保消息有稳定 key（虚拟滚动需要）
			if (!msgInfo.id && !msgInfo.tmpID && !msgInfo.tmpId && !msgInfo._key) {
				msgInfo._key = 'k_' + Date.now() + '_' + Math.random().toString(36).slice(2);
			}
			// 保持时间有序插入：WS 消息可能乱序到达（同秒多条/网络重排），
			// 直接 push 会破坏数组有序性，连锁影响增量游标与冷页归档边界
			const msgs = chat.messages;
			const sendTime = msgInfo.sendTime || 0;
			if (msgs.length === 0 || (msgs[msgs.length - 1].sendTime || 0) <= sendTime) {
				msgs.push(msgInfo);
			} else {
				// 二分查找插入位置（时间升序）
				let lo = 0, hi = msgs.length;
				while (lo < hi) {
					const mid = (lo + hi) >> 1;
					if ((msgs[mid].sendTime || 0) <= sendTime) lo = mid + 1;
					else hi = mid;
				}
				msgs.splice(lo, 0, msgInfo);
			}
			chat.stored = false;
			// 新消息改变了 latestMsgSendTime，需重排 chats 并重建索引
			if (!this.loading && this.chats.length > 1) {
				// 短路判断：若该会话已在第 0 位且 latestMsgSendTime ≥ 第 1 位，无需重排
				let needResort = true;
				const idx = this.chatIndexMap[chat.conversationID];
				if (idx === 0) {
					const secondTime = this.chats[1]?.latestMsgSendTime || 0;
					if (chat.latestMsgSendTime >= secondTime) {
						needResort = false;
					}
				}
				if (needResort) this._resortChats();
			}
			this.saveToStorage('meta', chat.conversationID);
		},
		// 更新消息
		updateMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			let message = this.findMessage(chat, msgInfo);
			if (message) {
				// 属性拷贝
				Object.assign(message, msgInfo);
				chat.stored = false;
				this.saveToStorage('meta', chat.conversationID);
			}
		},
		deleteMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat || !Array.isArray(chat.messages)) return;
			let delIdx = -1;
			for (let idx in chat.messages) {
				// 已经发送成功的，根据id删除
				if (chat.messages[idx].id && chat.messages[idx].id == msgInfo.id) {
					delIdx = idx;
					break;
				}
				// 正在发送中的消息可能没有id，只有临时id
				if (chat.messages[idx].tmpID && chat.messages[idx].tmpID == msgInfo.tmpID) {
					delIdx = idx;
					break;
				}
			}
			if (delIdx >= 0) {
				chat.messages.splice(delIdx, 1);
				if (delIdx < (chat.hotMinIdx || 0)) {
					chat.hotMinIdx--;
				}
				if (delIdx < (chat.readedMessageIdx || 0)) {
					chat.readedMessageIdx--;
				}
				chat.stored = false;
				this.saveToStorage('meta', chat.conversationID);
			}
		},
		recallMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) return;
			// 要撤回的消息id
			let id = msgInfo.content;
			let name = msgInfo.selfSend ? '你' : (chat.convType === 'single' ? '对方' : msgInfo.sendNickName);
			for (let idx in chat.messages) {
				let m = chat.messages[idx];
				if (m.id && m.id == id) {
					// 改造成一条提示消息（保存原始状态后再覆盖，用于未读数扣减判断）
					const origStatus = m.status;
					m.status = MESSAGE_STATUS.RECALL;
					m.content = name + "撤回了一条消息";
					m.msgType = MESSAGE_TYPE.TIP_TEXT
					// 会话列表
					chat.latestMsg = m.content;
					chat.latestMsgSendTime = msgInfo.sendTime;
					// 仅当被撤回的消息未被读过才扣减未读数
					if (!msgInfo.selfSend && origStatus != MESSAGE_STATUS.READED && chat.unreadCount > 0) {
						chat.unreadCount--;
						const gid = effectiveGroupID(chat);
						if (this._groupSummary[gid]) {
							this._groupSummary[gid].unreadCount = Math.max(0,
								(this._groupSummary[gid].unreadCount || 0) - 1);
						}
					}
				}
				// 被引用的消息也要撤回
				if (m.quoteMessage && m.quoteMessage.id == id) {
					m.quoteMessage.content = "引用内容已撤回";
					m.quoteMessage.status = MESSAGE_STATUS.RECALL;
					m.quoteMessage.msgType = MESSAGE_TYPE.TIP_TEXT
				}
			}
			chat.stored = false;
			if (!this.loading && this.chats.length > 1) {
				let needResort = true;
				const idx = this.chatIndexMap[chat.conversationID];
				if (idx === 0) {
					const secondTime = this.chats[1]?.latestMsgSendTime || 0;
					if (chat.latestMsgSendTime >= secondTime) needResort = false;
				}
				if (needResort) this._resortChats();
			}
			this.saveToStorage('meta', chat.conversationID);
		},
		updateChatFromFriend(friend) {
			let chat = this.findChatByFriend(friend.userID);
			if (chat && (chat.faceURL != friend.avatar ||
					chat.showName != friend.nickname)) {
				chat.faceURL = friend.avatar;
				chat.showName = friend.nickname;
				chat.stored = false;
				this.saveToStorage("meta", chat.conversationID);
			}
		},
		updateChatFromUser(user) {
			let chat = this.findChatByFriend(user.id);
			if (chat && (chat.faceURL != user.headImageThumb ||
					chat.showName != user.nickName)) {
				chat.faceURL = user.headImageThumb;
				chat.showName = user.nickName;
				chat.stored = false;
				this.saveToStorage('meta', chat.conversationID);
			}
		},
		updateChatFromGroup(group) {
			let chat = this.findChatByGroup(group.groupID);
			if (!chat) return;
			let changed = false;
			if (group.nickname && chat.showName != group.nickname) {
				chat.showName = group.nickname;
				changed = true;
			}
			// 群头像回填（群列表/群详情返回 avatar 时同步会话 faceURL）
			if (group.avatar && chat.faceURL != group.avatar) {
				chat.faceURL = group.avatar;
				changed = true;
			}
			// 群人数回填（会话标题/列表展示依赖 groupMemberCount）
			if (group.total && chat.groupMemberCount != group.total) {
				chat.groupMemberCount = group.total;
				changed = true;
			}
			if (changed) {
				chat.stored = false;
				this.saveToStorage("meta", chat.conversationID);
			}
		},
		setLoading(loading, silent = false) {
			this.loading = loading;
			// silent（周期兜底同步）：loading 仅保留数据面语义（cacheChats 查找/WS
			// 缓冲/索引与落盘守卫），silentLoading 让 Chat 页跳过"消息接收中"占位与高度
			// 缓存重建，消除每 60s 的列表卸载重建闪烁
			this.silentLoading = !!(loading && silent);
		},
		setDnd(chatInfo, isDnd) {
			let chat = this.findChat(chatInfo);
			if (chat) {
				const oldDnd = !!chat.isDnd;
				chat.isDnd = isDnd;
				// 本地切换时间戳：syncChats 在 10s 内短路服务端旧快照覆盖（防止开关回弹）
				chat._dndAt = Date.now();
				// 屏蔽时冻结未读移出分组汇总、恢复时加回，与会话角标/标题未读口径一致
				// （屏蔽前的存量未读不因屏蔽而消失，恢复提醒后仍可看到）
				if (oldDnd !== isDnd) {
					const gid = effectiveGroupID(chat);
					const unread = chat.unreadCount || 0;
					if (this._groupSummary[gid] && unread > 0) {
						this._groupSummary[gid].unreadCount = Math.max(0,
							(this._groupSummary[gid].unreadCount || 0) + (isDnd ? -unread : unread));
					}
				}
				// 标记脏数据并持久化，避免刷新后 IndexedDB meta 回退旧免打扰状态
				chat.stored = false;
				this.saveToStorage("meta", chat.conversationID);
			}
		},
		// 刷新会话列表并重新保存数据
		refreshChats() {
			// 取消任何待执行的 rAF 排序（本方法会同步排序，避免重复）
			this._resortDirty = false;
			this._resortPending = false;
			let chats = cacheChats || this.chats;
			chats.sort((chat1, chat2) => chat2.latestMsgSendTime - chat1.latestMsgSendTime);
			// 将消息一次性装载回来
			this.chats = chats;
			cacheChats = null;
			this._rebuildIndex();
			// 纵深防御：激活会话对象若被同步替换或已被清理时按 conversationID 重绑，
			// 防止 ChatBox 展示对象与 findChat/insertMessage 命中的对象分裂
			// （表现为打开中会话收不到/不显示新消息）；会话确已清理则关闭激活态
			if (this.activeChat && this.activeChat.conversationID) {
				const bound = this.chats.find(c => c && c.conversationID === this.activeChat.conversationID);
				if (bound !== this.activeChat) {
					if (bound) {
						this.activeChat = bound;
					} else {
						console.warn('[chatStore] refreshChats 激活会话不在新列表中，关闭激活态:', this.activeChat.conversationID);
						this.activeChat = null;
					}
				}
			}
			// 性能优化：全量校验 _groupSummary 防止增量计数漂移
			this._validateGroupSummary();
			this.saveToStorage();
		},
		// 性能优化：全量校验 _groupSummary 防止增量计数漂移
		_validateGroupSummary() {
			const correct = {};
			this.chats.forEach(chat => {
				if (!chat || chat.delete) return;
				const gid = effectiveGroupID(chat);
				if (!correct[gid]) correct[gid] = { chatCount: 0, unreadCount: 0 };
				correct[gid].chatCount++;
				// 免打扰会话的未读不参与分组汇总（与 Home.vue 标题未读口径一致），
				// 否则全量重建会把 setDnd 冻结的未读重新计入，恢复提醒时再叠加造成双计
				if (!chat.isDnd) {
					correct[gid].unreadCount += chat.unreadCount || 0;
				}
			});
			this._groupSummary = correct;
		},
		filterMessage(chats, maxTotalSize, maxPerChatSize) {
			// 每个会话只保留maxPerChatSize条消息
			// 裁剪额度只能取「已真实归档到冷页」的 _archivedCount：被裁段在冷页中
			// 已有副本，删除内存副本不会丢消息。不能用本轮将归档量预估——归档动作
			// 在本轮保存的裁剪之后才执行，递归减半 maxPerChatSize 时未归档消息会被
			// 裁掉且永远不再归档，造成消息永久丢失
			let remainTotalSize = 0;
			chats.forEach(chat => {
				if (!Array.isArray(chat.messages)) {
					chat.messages = [];
					return;
				}
				const canTrim = chat._archivedCount || 0;
				const targetLen = Math.max(maxPerChatSize, chat.messages.length - canTrim);
				if (chat.messages.length > targetLen) {
					let trim = chat.messages.length - targetLen;
					chat.messages = chat.messages.slice(chat.messages.length - targetLen);
					// 序列头部被裁掉 trim 条，已归档前缀副本量同步缩减，
					// 保证 _archivedCount 始终等于「当前序列前缀在冷页中的副本量」
					chat._archivedCount = Math.max(0, canTrim - trim);
				}
				remainTotalSize += chat.messages.length;
			})
			// 保证消息总数不超过maxTotalSize条，否则继续清理
			if (remainTotalSize > maxTotalSize && maxPerChatSize > 1) {
				this.filterMessage(chats, maxTotalSize, Math.max(1, Math.floor(maxPerChatSize / 2)));
			}
		},
		saveToStorage(scope = 'all', conversationID = null) {
			if (this.loading) {
				return;
			}
			// 记录脏数据范围
			if (conversationID) {
				if (!this.dirtyTracker[conversationID]) {
					this.dirtyTracker[conversationID] = new Set();
				}
				if (scope === 'all') {
					this.dirtyTracker[conversationID].add('meta');
					this.dirtyTracker[conversationID].add('hot');
				} else {
					this.dirtyTracker[conversationID].add(scope);
				}
			}
			if (_saveDebounceTimer) clearTimeout(_saveDebounceTimer);
			_saveDebounceTimer = setTimeout(() => {
				_saveDebounceTimer = null;
				if (typeof requestIdleCallback !== 'undefined') {
					requestIdleCallback(() => this._executeSave(), { timeout: 2000 });
				} else {
					this._executeSave();
				}
			}, 500);
		},
		// 页面关闭前强制刷新待保存数据
		flushSave() {
			if (_saveDebounceTimer) {
				clearTimeout(_saveDebounceTimer);
				_saveDebounceTimer = null;
			}
			return Promise.resolve(this._executeSave());
		},
		_executeSave() {
			const userStore = useUserStore();
			let userId = userStore.userInfo.id;
			let adminId = userStore.userInfo.adminID;
			let name = userStore.userInfo.name;
			// 守卫：userInfo 未加载完成时跳过保存，防止多账号 key 碰撞
			if (!userId || !name) {
				console.warn('[chatStore] _executeSave 跳过：userInfo 未就绪', { userId, adminId, name });
				return;
			}
			let base = buildKey(userId, adminId, name);

			// 防止 loadChat 完成前刷新导致索引被清空
			// 若 this.chats 已写入但 syncChats 尚未完成后的 refreshChats 为 cacheChats，
			// 则页面关闭时缓存同步数据丢失——临时用 cacheChats 保存防止丢失
			let chats = this.chats;
			if ((!chats || chats.length === 0) && cacheChats && cacheChats.length > 0) {
				chats = cacheChats;
				console.log('[chatStore] _executeSave 使用 cacheChats 防止数据丢失');
			}
			if (!chats || chats.length === 0) return;
			// 应用消息上限裁剪
			this.filterMessage(chats, MAX_TOTAL_MSGS, MAX_CHAT_MSGS);

			// 性能优化：chatKeys 始终包含全部非删除会话（IndexedDB 索引不能丢失），
			// 脏数据过滤只控制 meta/hot 写入，避免不必要的 IndexedDB I/O。
			let chatKeys = [];
			const writeTasks = [];
			let hasAnyDirty = false;

			for (let idx in chats) {
				let chat = chats[idx];
				let convID = chat.conversationID;
				let dirty = this.dirtyTracker[convID];
				let isChanged = chat.delete || chat.stored === false || !!dirty;

				// 会话有实际写入需求（stored=false/脏标记）时撤销幽灵清理标记：
				// 覆盖 setPinned/remarkChat 等所有写路径，防止数据以"无索引孤儿 key"写回
				if (isChanged && chat._ghostCleaned) {
					chat._ghostCleaned = false;
				}

				if (chat.delete) {
					writeTasks.push(Promise.all([
						localForage.removeItem(metaKey(base, convID)),
						localForage.removeItem(hotKey(base, convID)),
						localForage.removeItem(coldIdxKey(base, convID)),
						localForage.removeItem(`${base}-${convID}`),
					]));
					delete this.dirtyTracker[convID];
					continue;
				}

				// 始终记录 key 到索引（防止非脏会话从 IndexedDB 索引中消失）；
				// 已清理的幽灵会话除外（其 meta/hot 已删除，索引保留会指向陈旧记录）
				let metaKeyName = metaKey(base, convID);
				if (!chat._ghostCleaned) {
					chatKeys.push(metaKeyName);
				}

				// 脏过滤：未变化且无迁移需求的会话跳过 meta/hot 写入
				if (!isChanged && !chat._needsMigration) continue;
				hasAnyDirty = true;

				// 每会话独立写入任务：任一关键写入（含冷页归档）失败时
				// 保留脏标记，下次保存自动重试，避免中间段消息永久丢失
				writeTasks.push((async () => {
					let ok = true;
					try {
						// 检测旧格式数据，触发懒迁移
						if (chat._needsMigration) {
							await this._migrateConversation(chat, base, convID);
						}

						// 写入 meta（不含 messages 数组）
						let writeMeta = !dirty || dirty.has('meta') || dirty.has('hot') || chat.stored === false || chat._needsMigration;
						if (writeMeta) {
							await localForage.setItem(metaKeyName, extractMeta(chat));
						}

						// 写入 hot（仅消息变更时写入最后 MAX_HOT_MSGS 条）
						let writeHot = (dirty && dirty.has('hot')) || chat.stored === false || chat._needsMigration;
						if (writeHot) {
							let hotMsgs = [];
							if (Array.isArray(chat.messages) && chat.messages.length > 0) {
								hotMsgs = chat.messages.slice(-MAX_HOT_MSGS);
							}
							// 未加载会话（_hotLoaded 非 true）的内存消息可能仅含增量同步残留：消息按需加载，
							// 打开会话前的 messages 只有 WS/批量同步写入的新消息。直接写入会用「部分消息」
							// 覆盖完整历史缓存（实测 hot 从 8 条被 1 条增量覆盖），必须先与旧 hot 合并，
							// 保证只增不减；已加载会话（_hotLoaded=true）内存为权威数据，按既有逻辑直接写
							if (chat._hotLoaded !== true) {
								try {
									const oldHot = await localForage.getItem(hotKey(base, convID));
									if (Array.isArray(oldHot) && oldHot.length > 0) {
										// 内存副本优先：同一消息以内存为准（已读回执/撤回等状态更新不被旧缓存回退），
										// 仅将内存缺失的历史消息由旧 hot 补充，保证只增不减且最新状态获胜
										const freshKeys = new Set();
										hotMsgs.forEach(m => {
											const k = m && (m.id || m._key);
											if (k) freshKeys.add(k);
										});
										const keptOld = oldHot.filter(m => {
											const k = m && (m.id || m._key);
											return !k || !freshKeys.has(k);
										});
										hotMsgs = mergeSortedMessages(keptOld, hotMsgs).slice(-MAX_HOT_MSGS);
									}
								} catch (e) {
									// 旧热区读取失败：按内存内容写入（原行为兜底），不阻塞保存流程
									console.warn('[chatStore] 读取旧 hot 失败，按内存内容写入:', e?.message || e);
								}
							}
							await localForage.setItem(hotKey(base, convID), hotMsgs);

							// 冷页归档：失败时返回 false，由外层保留脏标记重试
							if (Array.isArray(chat.messages) && chat.messages.length > MAX_HOT_MSGS) {
								ok = await this._archiveColdPage(chat, base, convID).catch(() => false);
							}
						}
					} catch (e) {
						console.error('[chatStore] 会话写入失败，保留脏标记待重试:', convID, e?.message || e);
						ok = false;
					}
					if (ok) {
						chat.stored = true;
						chat._needsMigration = false;
						delete this.dirtyTracker[convID];
					}
					return ok;
				})());
			}

			// 没有任何脏数据且无删除操作，跳过存储写入（chatKeys 也不需要更新）
			if (!hasAnyDirty && writeTasks.length === 0) return;

			let chatsData = {
				privateMsgMaxId: this.privateMsgMaxId,
				groupMsgMaxId: this.groupMsgMaxId,
				chatKeys: chatKeys,
				schemaVersion: this.schemaVersion,
			}
			Promise.all(writeTasks).then(() => {
				localForage.setItem(base, chatsData)
			}).catch((e) => {
				console.error('[chatStore] IndexedDB 保存失败:', e?.message || e);
			})
			// 清理已删除的会话
			this.chats = this.chats.filter(chat => !chat.delete)
			this._rebuildIndex();
		},

		// 懒迁移：旧格式完整 chat key → 拆分 meta + hot + cold
		_migrateConversation(chat, base, convID) {
			let oldKey = `${base}-${convID}`;
			return localForage.getItem(oldKey).then(oldChat => {
				if (!oldChat || !Array.isArray(oldChat.messages)) return;
				let msgs = oldChat.messages;
				let writes = [];
				if (msgs.length > MAX_HOT_MSGS) {
					let coldMsgs = msgs.slice(0, msgs.length - MAX_HOT_MSGS);
					freezeMessages(coldMsgs);
					let pageIdx = 0;
					while (coldMsgs.length > 0) {
						writes.push(localForage.setItem(coldPageKey(base, convID, pageIdx), coldMsgs.slice(0, COLD_PAGE_SIZE)));
						coldMsgs = coldMsgs.slice(COLD_PAGE_SIZE);
						pageIdx++;
					}
					writes.push(localForage.setItem(coldIdxKey(base, convID), {
						totalPages: pageIdx,
						messageCount: oldChat.messages.length,
						oldestTime: msgs[0]?.sendTime || 0,
						newestTime: msgs[msgs.length - 1]?.sendTime || 0,
					}));
					chat.messages = msgs.slice(-MAX_HOT_MSGS);
				} else {
					chat.messages = msgs;
				}
				// 全部冷页/索引写入成功后才删除旧 key：写入失败时旧数据仍在，
				// 保留 _needsMigration 下次重试，避免「新数据未落盘、旧数据已删」的永久丢失
				return Promise.all(writes).then(() => localForage.removeItem(oldKey));
			}).catch(e => {
				console.error('_migrateConversation failed:', convID, e);
				return Promise.reject(e);   // 迁移失败向上传递，保留 _needsMigration 重试
			});
		},

		// 归档超出 MAX_HOT_MSGS 的消息到冷页存储（追加模式，不覆盖已有页）
		// 返回 true=成功/无需归档，false=归档失败（调用方应保留脏标记以便重试）
		_archiveColdPage(chat, base, convID) {
			if (!Array.isArray(chat.messages) || chat.messages.length <= MAX_HOT_MSGS) return true;
			let overflow = chat.messages.slice(0, chat.messages.length - MAX_HOT_MSGS);
			if (overflow.length === 0) return true;
			// 性能优化：冻结冷消息，Vue 2 跳过响应式代理
			freezeMessages(overflow);

			return localForage.getItem(coldIdxKey(base, convID)).then(coldIdx => {
				let pageIdx = coldIdx && coldIdx.totalPages ? coldIdx.totalPages : 0;
				// 已归档量只看 _archivedCount（当前序列前缀在冷页的副本量）：
				// coldIdx.hotArchived 是历史累计值，序列被裁剪后必然高估，用它跳过写入
				// 会让「上轮 hot 区落入本轮 overflow 区」的消息漏归档，而 hot 随后被覆盖
				// 为最新 MAX_HOT_MSGS 条，导致这些消息永久丢失
				let alreadyArchived = chat._archivedCount || 0;
				let toArchive = overflow.slice(alreadyArchived);
				if (toArchive.length === 0) return true;

				// 收集全部冷页/索引写入并 Promise.all 等待：任一写入失败则整体返回 false，
				// 由调用方保留脏标记重试，避免「写入失败仍返回成功」导致已裁剪消息永久丢失
				let writes = [];
				while (toArchive.length > 0) {
					writes.push(localForage.setItem(coldPageKey(base, convID, pageIdx), toArchive.slice(0, COLD_PAGE_SIZE)));
					toArchive = toArchive.slice(COLD_PAGE_SIZE);
					pageIdx++;
				}

				let msgs = chat.messages;
				writes.push(localForage.setItem(coldIdxKey(base, convID), {
					totalPages: pageIdx,
					messageCount: msgs.length,
					hotArchived: overflow.length,
					oldestTime: msgs[0]?.sendTime || 0,
					oldestId: msgs[0]?.id || 0,
					newestTime: msgs[msgs.length - 1]?.sendTime || 0,
				}));
				return Promise.all(writes).then(() => {
					chat._archivedCount = overflow.length;
					return true;
				});
			}).catch((e) => {
				console.error('[chatStore] 冷页归档失败:', convID, e?.message || e);
				return false;
			});
		},

		// 按 latestMsgSendTime 降序重排 chats 并重建索引（新消息到达时调用）
		// 优化：用 rAF 将同一帧内的多次调用合并为一次排序
		_resortChats() {
			this._resortDirty = true;
			if (this._resortPending) return;
			this._resortPending = true;
			requestAnimationFrame(() => {
				this._resortPending = false;
				if (!this._resortDirty) return;
				this._resortDirty = false;
				this.chats = [...this.chats].sort((a, b) => (b.latestMsgSendTime || 0) - (a.latestMsgSendTime || 0));
				this._rebuildIndex();
			});
		},
		// 重建 conversationID → index 映射表（O(n)，仅在 chats 数组结构变化时调用）
		_rebuildIndex(chats) {
			const map = {};
			(chats || this.chats).forEach((chat, i) => {
				if (chat && chat.conversationID) {
					map[chat.conversationID] = i;
				}
			});
			this.chatIndexMap = map;
		},
		// 在 idx 位置插入后，将 >= idx 的索引全部 +delta
		_shiftIndexFrom(idx, delta) {
			const map = this.chatIndexMap;
			for (const convID in map) {
				if (map[convID] >= idx) {
					map[convID] += delta;
				}
			}
		},
		clear() {
			if (_saveDebounceTimer) {
				clearTimeout(_saveDebounceTimer);
				_saveDebounceTimer = null;
			}
			cacheChats = []
			this.chats = [];
			this.chatIndexMap = {};
			this.activeChat = null;
			this.privateMsgMaxId = 0;
			this.groupMsgMaxId = 0;
			this.loading = false;
			this.silentLoading = false;
		},
		// 注册 beforeunload 监听，防止关闭页面时丢数据
		initSaveGuard() {
			if (this._saveGuardInstalled) return;
			this._saveGuardInstalled = true;
			const flush = () => this.flushSave();
			window.addEventListener('beforeunload', flush);
			window.addEventListener('pagehide', flush);
			if (typeof document !== 'undefined') {
				document.addEventListener('visibilitychange', () => {
					if (document.visibilityState === 'hidden') this.flushSave();
				});
			}
		},
		// 批量读取 IndexedDB 指定 key：单只读事务内完成全部 get，
		// 规避逐 key getItem 的独立事务开销（万级会话时 loadChat 从数秒降至亚秒）。
		// 仅 IndexedDB 驱动可用；非 IDB 驱动或事务级异常返回 null，由调用方回退逐 key 读取。
		// 注意：直连 localforage 私有结构 _dbInfo（1.10.0 稳定），升级 localforage 需回归验证
		_bulkLoadChats(keys) {
			return localForage.ready().then(() => {
				if (!Array.isArray(keys) || keys.length === 0) return { metaMap: {}, failedKeys: [] };
				// 与 localForage.getItem 的 normalizeKey 语义对齐（非字符串 key cast 为字符串）
				const strKeys = keys.map(key => String(key));
				// 非 IndexedDB 驱动（WebSQL/localStorage/隐私模式回退）时走旧路径
				if (localForage.driver() !== localForage.INDEXEDDB) return null;
				const dbInfo = localForage._dbInfo;
				if (!dbInfo || !dbInfo.db || !dbInfo.storeName) return null;
				const db = dbInfo.db;
				return new Promise((resolve, reject) => {
					let tx;
					try {
						tx = db.transaction(dbInfo.storeName, 'readonly');
					} catch (e) {
						reject(e);
						return;
					}
					const store = tx.objectStore(dbInfo.storeName);
					const metaMap = {};
					const failedKeys = [];
					// 单事务内同步发起全部 get（事务在最后一个请求完成后自动提交）；
					// IDB 对不存在的 key 返回 undefined，统一映射为 null 与 getItem 语义一致。
					// 单个请求失败时 preventDefault 阻止 error 冒泡 abort 整个事务，
					// 仅记录失败 key，由调用方对失败 key 逐 key 补齐，避免全部结果作废回退慢路径
					strKeys.forEach(key => {
						const req = store.get(key);
						req.onsuccess = (e) => {
							const val = e.target.result;
							metaMap[key] = val === undefined ? null : val;
						};
						req.onerror = (e) => {
							e.preventDefault();
							failedKeys.push(key);
						};
					});
					tx.oncomplete = () => resolve({ metaMap, failedKeys });
					tx.onerror = () => reject(tx.error || new Error('IndexedDB 批量读取失败'));
					tx.onabort = () => reject(tx.error || new Error('IndexedDB 批量读取中止'));
				});
			}).catch(e => {
				// 降级留痕：批量读失败回退逐 key getItem 时必须可观测，避免线上性能回退不可诊断
				console.warn('[chatStore] 批量读取降级为逐 key getItem:', e?.message || e);
				return null;
			});
		},
		loadChat() {
			return new Promise((resolve, reject) => {
				let userStore = useUserStore();
				let userId = userStore.userInfo.id;
				let adminId = userStore.userInfo.adminID;
				let name = userStore.userInfo.name;
				// 守卫：userInfo 未加载完成时跳过，防止多账号 key 碰撞
				if (!userId || !name) {
					console.warn('[chatStore] loadChat 跳过：userInfo 未就绪', { userId, adminId, name });
					resolve();
					return;
				}
				let base = buildKey(userId, adminId, name);
				localForage.getItem(base).then((chatsData) => {
					if (!chatsData) {
						resolve();
						return;
					}
					if (!chatsData.chatKeys || chatsData.chatKeys.length === 0) {
						resolve();
						return;
					}
					// 检测 schema 版本：v2 key 以 -meta 结尾
					let isV2 = chatsData.schemaVersion >= 2 || chatsData.chatKeys[0].endsWith('-meta');
					// 批量读（IDB 单事务）；非 IDB 驱动或事务级异常时回退逐 key getItem，
					// 结果统一为 key → meta 映射，组装逻辑与旧实现等价
					const loadMetaMap = () => this._bulkLoadChats(chatsData.chatKeys).then(result => {
						// 整体降级：非 IDB 驱动或事务级异常，批量结果不可用，全部逐 key 读取
						if (!result) {
							const map = {};
							const promises = chatsData.chatKeys.map(key =>
								localForage.getItem(key).then(meta => { map[key] = meta; })
							);
							return Promise.all(promises).then(() => map);
						}
						const { metaMap, failedKeys } = result;
						// 部分失败：仅对失败 key 逐 key 补齐，保留批量结果并留痕定位坏 key；
						// 单个补齐失败不拖垮整体（坏 key 大概率持续失败），跳过该会话与"缺 key 即无会话"语义一致
						if (failedKeys.length > 0) {
							console.warn('[chatStore] 批量读取部分 key 失败，逐 key 补齐:', failedKeys);
							const promises = failedKeys.map(key =>
								localForage.getItem(key)
									.then(meta => { metaMap[key] = meta; })
									.catch(err => {
										console.warn('[chatStore] 补齐失败，跳过该会话 key:', key, err?.message || err);
									})
							);
							return Promise.all(promises).then(() => metaMap);
						}
						return metaMap;
					});
					loadMetaMap().then(metaMap => {
						const chats = [];
						chatsData.chatKeys.forEach(key => {
							const meta = metaMap[key];
							if (!meta) return;
							if (isV2) {
								// v2：meta 不含 messages，置空数组并标记已存储
								meta.messages = [];
								meta.stored = true;
							}
							chats.push(meta);
						});
						chatsData.chats = chats.filter(chat => {
							return chat && chat.conversationID && chat.conversationID.trim() !== '';
						});
						if (!isV2) {
							// v1（旧格式）：全量加载，标记迁移
							chatsData.chats.forEach(c => { c._needsMigration = true; c.stored = false; if (!c.convType && c.type) { c.convType = c.type === 'GROUP' ? 'group' : 'single'; } });
						}
						this.initChats(chatsData);
						resolve();
					}).catch(e => {
						console.error('[chatStore] IndexedDB 加载失败:', e?.message || e);
						reject(e);
					})
				}).catch(e => {
					console.error('[chatStore] IndexedDB 加载失败:', e?.message || e);
					reject(e);
				})
			})
		},

				// 加载标签数据
		loadChatlabels(){
			return new Promise((resolve, reject) => {
				getConversationLabels().then((labels) => {
					this.setChatLables(labels);
					resolve();
				}).catch((res) => {
					reject(res);
				});
			})
		},
		// 加载快捷回复
		loadQuickReplies() {
			return new Promise((resolve, reject) => {
				getQuickReplyList().then((replies) => {
					this.setQuickReplies(replies);
					resolve();
				}).catch((res) => {
					reject(res);
				});
			})
		},
		// 加载自动回复话术
		loadAutoReplyScripts() {
			return new Promise((resolve, reject) => {
				getAutoReplyScripts().then((scripts) => {
					this.setAutoReplyScripts(scripts)
					resolve()
				}).catch((res) => {
					reject(res)
				})
			})
		},

		// 加载超时跟进配置
		loadFollowUpConfig() {
			return new Promise((resolve, reject) => {
				getAutoFollowUpConfig().then((config) => {
					this.setFollowUpConfig(config)
					resolve()
				}).catch((res) => {
					reject(res)
				})
			})
		},

		// 清理幽灵会话的本地残留（后端已确认会话不存在，conversationExists=false）：
		// 删除 IndexedDB 的 meta/hot/coldIdx/冷页文件并移除 chatKeys 索引，
		// 同时清除脏标记防止 _executeSave 把残留重新写回；内存会话保留以展示空态
		// 批量清理本地归档/删除会话的 IndexedDB 残留（syncChats 调用）：
		// 与 _cleanupGhostConversation 语义一致，但只扫描一次冷页 key 收集全部待删 key，
		// 避免 N 个会话各扫一次 keys() 的 O(N×M) 开销（几千会话时非常慢）；
		// 分批执行 removeItem 防止一次性数万事务拖卡浏览器
		async _cleanupGhostConversations(base, conversationIDs) {
			// 一次扫描收集全部待删 key（meta/hot/coldIdx/旧格式 + 冷页）
			let allKeys = [];
			try {
				allKeys = await localForage.keys();
			} catch (e) {
				console.warn('[chatStore] 清理前 keys 扫描失败:', e?.message || e);
			}
			const coldPrefixes = new Set(conversationIDs.map(id => `${base}-${id}-cold-`));
			const marker = '-cold-';
			const toRemove = [];
			conversationIDs.forEach(id => {
				toRemove.push(
					metaKey(base, id),
					hotKey(base, id),
					coldIdxKey(base, id),
					`${base}-${id}`, // 旧格式
				);
			});
			(allKeys || []).forEach(k => {
				if (typeof k !== 'string' || !k.startsWith(base)) return;
				const idx = k.indexOf(marker, base.length);
				if (idx > 0 && coldPrefixes.has(k.slice(0, idx + marker.length))) {
					toRemove.push(k);
				}
			});
			// 分批 removeItem（每批 800 个 key），失败跳过不阻塞后续
			const BATCH = 800;
			for (let i = 0; i < toRemove.length; i += BATCH) {
				const batch = toRemove.slice(i, i + BATCH);
				try {
					await Promise.all(batch.map(k => localForage.removeItem(k)));
				} catch (e) {
					console.warn('[chatStore] 清理会话残留失败:', e?.message || e);
				}
			}
			// 从 chatKeys 索引移除（保存路径在无脏数据时会短路，这里手动同步，仅一次）
			try {
				const chatsData = await localForage.getItem(base);
				if (chatsData && Array.isArray(chatsData.chatKeys)) {
					const metaKeys = new Set(conversationIDs.map(id => metaKey(base, id)));
					const before = chatsData.chatKeys.length;
					chatsData.chatKeys = chatsData.chatKeys.filter(k => !metaKeys.has(k));
					if (chatsData.chatKeys.length !== before) {
						await localForage.setItem(base, chatsData);
					}
				}
			} catch (e) {
				console.warn('[chatStore] 更新 chatKeys 索引失败:', e?.message || e);
			}
			console.warn(`[chatStore] 已批量清理归档会话本地残留 ${conversationIDs.length} 个`);
		},

		async _cleanupGhostConversation(base, conversationID) {
			const chat = this.findChat({ conversationID });
			// 防写回必须先于删除生效：删除操作是异步的，若 _executeSave（debounce+idle）
			// 在窗口内看到脏标记/stored=false，会把残留重新写回，清理失效
			if (chat) {
				delete this.dirtyTracker[conversationID];
				chat.stored = true;
				// 标记已清理：_executeSave 重建 chatKeys 索引时跳过该会话，
				// 防止陈旧索引指向已删除的 meta
				chat._ghostCleaned = true;
			}
			try {
				const removeTasks = [
					localForage.removeItem(metaKey(base, conversationID)),
					localForage.removeItem(hotKey(base, conversationID)),
					localForage.removeItem(coldIdxKey(base, conversationID)),
					localForage.removeItem(`${base}-${conversationID}`), // 旧格式
				];
				// 冷页范围：不依赖内存 _coldPageCount 或冷页索引（hot 走 API 回退时两者都可能缺失，
				// 缺失会导致 pageCount=0、历史冷页成为孤儿 key 永久残留）——直接扫描 IndexedDB 中
				// 该会话的冷页 key 按前缀全量删除，从根上杜绝孤儿残留
				const coldPagePrefix = `${base}-${conversationID}-cold-`;
				try {
					const allKeys = await localForage.keys();
					(allKeys || []).forEach(k => {
						if (k.indexOf(coldPagePrefix) === 0) {
							removeTasks.push(localForage.removeItem(k));
						}
					});
				} catch (e) {
					// keys() 不可用时回退按索引范围删除（_coldPageCount 与 coldIdx 兜底）
					console.warn('[chatStore] 冷页 key 扫描失败，回退按索引删除:', e?.message || e);
					const coldIdx = await localForage.getItem(coldIdxKey(base, conversationID));
					const pageCount = Math.max(chat?._coldPageCount || 0, coldIdx?.totalPages || 0);
					for (let p = 0; p <= pageCount; p++) {
						removeTasks.push(localForage.removeItem(coldPageKey(base, conversationID, p)));
					}
				}
				await Promise.all(removeTasks);
				// 从 chatKeys 索引移除（正常保存路径在无脏数据时会短路，这里手动同步）
				const chatsData = await localForage.getItem(base);
				if (chatsData && Array.isArray(chatsData.chatKeys)) {
					const metaK = metaKey(base, conversationID);
					if (chatsData.chatKeys.includes(metaK)) {
						chatsData.chatKeys = chatsData.chatKeys.filter(k => k !== metaK);
						await localForage.setItem(base, chatsData);
					}
				}
				console.warn('[chatStore] 已清理幽灵会话本地残留:', conversationID);
			} catch (e) {
				console.warn('[chatStore] 清理幽灵会话残留失败:', conversationID, e?.message || e);
			}
		},

		// 加载会话热消息（从 IndexedDB -hot key 或 API 回退）
		async loadHotMessages(conversationID) {
			const chat = this.findChat({ conversationID });
			if (!chat) return;
			const userStore = useUserStore();
			const base = buildKey(userStore.userInfo.id, userStore.userInfo.adminID, userStore.userInfo.name);

			try {
				// 优先从 IndexedDB 读取 hot 消息
				const hotMsgs = await localForage.getItem(hotKey(base, conversationID));
				if (hotMsgs && Array.isArray(hotMsgs) && hotMsgs.length > 0) {
					// 去重合并而非覆盖：防止加载期间 WS 新消息先插入 chat.messages 而被覆盖丢失
					const existing = Array.isArray(chat.messages) ? chat.messages : [];
					const existKeys = new Set();
					existing.forEach(m => {
						const key = m && (m.id || m._key);
						if (key) existKeys.add(key);
					});
					const fresh = hotMsgs.filter(m => m && !existKeys.has(m.id || m._key));
					chat.messages = mergeSortedMessages(existing, fresh);
					// 保持可探查状态：是否还有更早历史由 loadColdPage 向服务器确认
					chat._allColdLoaded = false;
					// 读取冷页索引
					const coldIdx = await localForage.getItem(coldIdxKey(base, conversationID));
					if (coldIdx) {
						chat._coldPageCount = coldIdx.totalPages || 0;
						chat._coldOldestTime = coldIdx.oldestTime || 0;
						chat._coldOldestId = coldIdx.oldestId || 0;
						// 不能恢复 coldIdx.hotArchived：它是历史累计值（冷页副本总量），而当前
						// messages 只有 hot 区（不在冷页），恢复必然虚高，filterMessage 会按
						// 虚高额度裁掉未归档消息；初始化为 0，保存时 _archiveColdPage 重新
						// 归档后自然校正
						chat._archivedCount = 0;
					}
					chat._hotLoaded = true;
					// 仅当无待写脏数据时才标记已存储；否则保持 stored=false，
					// 避免覆盖 insertMessage/updateMessage 的脏标记（它们只标记 'meta'），
					// 导致 hot 不落盘：meta 已更新最新时间但消息只存内存，刷新后 syncChats 判定无更新、
					// loadHotMessages 又命中旧 hot 不触发 API fallback，新消息永久丢失
					if (!this.dirtyTracker[conversationID] || this.dirtyTracker[conversationID].size === 0) {
						chat.stored = true;
					}
					return;
				}
			} catch (e) { /* IndexedDB miss, fall through to API */ }

			// 回退到 API
			try {
				const { getMessages } = await import('../api/message.js');
				const data = await getMessages({ conversationID, lastMessageTime: 0, syncType: 'full' });
				if (data && data.list && data.list.length > 0) {
					this.addMessagesToConversation(conversationID, data.list);
				} else if (data && data.conversationExists === false) {
					// 后端显式确认该会话不存在（区别于"存在但无消息"的空列表）：
					// 清理本地残留的幽灵会话
					await this._cleanupGhostConversation(base, conversationID);
				}
				// 服务器可能还有更早消息，交由 loadColdPage 分页探查
				chat._allColdLoaded = false;
				// 成功响应（含空列表）视为加载完成
				chat._hotLoaded = true;
			} catch (e) {
				// 403/404 属确定性失败（无权限/不存在）：置终态避免每次打开都重复请求并弹错误提示；
				// 网络/5xx 等瞬时失败不置 _hotLoaded，允许下次打开会话时重试，避免失败一次后该会话永久空白
				const status = e && e.response && e.response.status;
				if (status === 403 || status === 404) {
					chat._hotLoaded = true;
				} else {
					console.error('loadHotMessages API fallback failed:', e);
				}
			}
		},

		// 加载冷消息页（IndexedDB 优先，API 兜底），返回新增消息数
		async loadColdPage(conversationID) {
			const chat = this.findChat({ conversationID });
			console.warn('[chatStore] loadColdPage start', { conversationID, hasChat: !!chat, _allColdLoaded: chat?._allColdLoaded, hotCount: chat?.messages?.length });
			if (!chat || chat._allColdLoaded) return 0;

			// 去重
			if (!this.coldPageInFlight[conversationID]) {
				this.coldPageInFlight[conversationID] = new Set();
			}
			const pageIdx = chat._coldPageLoaded || 0;
			if (this.coldPageInFlight[conversationID].has(pageIdx)) return 0;
			this.coldPageInFlight[conversationID].add(pageIdx);

			const userStore = useUserStore();
			const base = buildKey(userStore.userInfo.id, userStore.userInfo.adminID, userStore.userInfo.name);

			try {
				let coldMsgs = await localForage.getItem(coldPageKey(base, conversationID, pageIdx));

				if (!coldMsgs || coldMsgs.length === 0) {
					// API 兜底：游标取自"已加载的最旧消息"，随分页推进
					const cursor = chat._coldOldestTime || chat.messages[0]?.sendTime || 0;
					const cursorID = chat._coldOldestId || chat.messages[0]?.id || 0;
				console.warn('[chatStore] loadColdPage API fallback', { conversationID, cursor, cursorID, msg0Time: chat.messages[0]?.sendTime, msgLastTime: chat.messages[chat.messages.length-1]?.sendTime, _coldOldestTime: chat._coldOldestTime });
					const { getMessagesPage } = await import('../api/message.js');
					const data = await getMessagesPage({
						conversationID,
						cursor,
						cursorID,
						direction: 'older',
						limit: COLD_PAGE_SIZE,
					});

					console.warn('[chatStore] loadColdPage API response', { hasData: !!data, listLen: data?.list?.length, hasMore: data?.hasMore });
					if (data && data.list && data.list.length > 0) {
						coldMsgs = data.list;
						// 缓存到 IndexedDB（失败不阻塞主流程：内存游标与消息合并照常推进，
						// 下次打开会话该页缓存缺失时会走 API fallback 重新拉取）
						try {
							await localForage.setItem(coldPageKey(base, conversationID, pageIdx), coldMsgs);
							// 更新冷页索引
							const coldIdx = await localForage.getItem(coldIdxKey(base, conversationID));
							await localForage.setItem(coldIdxKey(base, conversationID), {
								totalPages: Math.max(coldIdx?.totalPages || 0, pageIdx + 1),
								messageCount: (coldIdx?.messageCount || 0) + coldMsgs.length,
								oldestTime: data.oldestTime || coldMsgs[0]?.sendTime || 0,
								oldestId: coldMsgs[0]?.id || 0,
								newestTime: coldIdx?.newestTime || 0,
							});
						} catch (e) {
							console.warn('[chatStore] 冷页缓存写入失败:', e?.message || e);
						}
						// 同步推进内存游标（正序数组第一条即最旧消息），
						// 下次 fallback 从新的最旧位置继续，避免重复拉取同一页导致历史加载卡死
						chat._coldOldestTime = data.oldestTime || coldMsgs[0]?.sendTime || 0;
						chat._coldOldestId = coldMsgs[0]?.id || 0;
					}
					if (!data || !data.hasMore) {
					console.warn('[chatStore] _allColdLoaded=true #1 API-no-more');
						chat._allColdLoaded = true;
						// 后端显式确认会话不存在（conversationExists=false，区别于
						// 真实会话历史顶端的空列表）：清理本地残留，避免下次刷新后
						// IndexedDB 残留再次被恢复
						if (data && data.conversationExists === false) {
							await this._cleanupGhostConversation(base, conversationID);
						}
					}
				}

				if (coldMsgs && coldMsgs.length > 0) {
					// 为每条冷消息分配稳定key，防止v-for :key fallthrough到不稳定的 idx
					coldMsgs.forEach(m => {
						if (!m.id && !m.tmpID && !m.tmpId && !m._key) {
							m._key = 'cold_' + (m.sendTime || '') + '_' + Math.random().toString(36).slice(2, 8);
						}
					});
					// 性能优化：冻结冷消息，Vue 2 跳过响应式代理
					freezeMessages(coldMsgs);
					// 去重：冷消息中可能包含已在热消息中的消息（如 MAX_HOT_MSGS 调大后旧冷页与热消息重合）
					const hotIds = new Set();
					(chat.messages || []).forEach(m => {
						const key = m.id || m._key;
						if (key) hotIds.add(key);
					});
					const dedupedCold = coldMsgs.filter(m => {
						const key = m.id || m._key;
						return !key || !hotIds.has(key);
					});
					// 整页都是重复消息：推进页码，继续探查下一页
					// 不再用本地 _coldPageCount 猜测终止——本地页读完后，下一页缓存缺失
					// 会自然走 API fallback，由服务器 hasMore=false/空返回收敛（#1/#3）
					if (dedupedCold.length === 0) {
						chat._coldPageLoaded = pageIdx + 1;
						this.coldPageInFlight[conversationID].delete(pageIdx);
						return 0;
					}
					// 不在此处替换 chat.messages，由调用方与 visStart/visEnd 原子更新，消除微任务中间态渲染
					const merged = mergeSortedMessages(dedupedCold, chat.messages);
					this.coldPageInFlight[conversationID].delete(pageIdx);
					return { coldMsgs: dedupedCold, merged, pageIdx: pageIdx + 1 };
				}

				chat._allColdLoaded = true;
			console.warn('[chatStore] _allColdLoaded=true #3 coldMsgs-empty');
			} catch (e) {
				// 403/404 属确定性失败（无权限/不存在）：置终态避免滚动/切换会话反复触发请求风暴
				// （loadHotMessages 已有同款终态语义，此处对齐）；网络/5xx 等瞬时失败不置位，
				// 允许下次触发重试，避免该会话历史加载永久停摆
				const status = e && e.response && e.response.status;
				if (status === 403 || status === 404) {
					chat._allColdLoaded = true;
					// 404=服务端确认会话不存在：清理本地残留的幽灵会话；
					// 403 不清理缓存——归属迁移/数据修复后会话可自动恢复
					if (status === 404) {
						await this._cleanupGhostConversation(base, conversationID);
					}
				} else {
					console.error('loadColdPage failed:', e);
				}
			} finally {
				this.coldPageInFlight[conversationID].delete(pageIdx);
			}
			return 0;
		},

	},
	getters: {
		findChats: (state) => () => {
			if (cacheChats && state.loading) {
				return cacheChats;
			}
			return state.chats;
		},
		findChatIdx: (state) => (chat) => {
			if (!chat || !chat.conversationID) return undefined;
			// 加载中使用 cacheChats，fallback 线性搜索
			if (cacheChats && state.loading) {
				for (let idx in cacheChats) {
					if (cacheChats[idx].conversationID == chat.conversationID) return idx;
				}
				return undefined;
			}
			const idx = state.chatIndexMap[chat.conversationID];
			if (idx !== undefined) {
				return idx;
			}
			// 索引陈旧（如会话被清理后索引重建间隙）兜底线性搜索：
			// 避免 chats[undefined] 静默返回 undefined 导致回显/更新链路丢失
			for (let i in state.chats) {
				if (state.chats[i] && state.chats[i].conversationID == chat.conversationID) return i;
			}
			return undefined;
		},
		findChat: (state) => (chat) => {
			let chats = state.findChats();
			let idx = state.findChatIdx(chat);
			return chats[idx];
		},
		findChatByFriend: (state) => (fid) => {
			let chats = state.findChats();
			return chats.find(chat => chat.personalUserID == fid)
		},
		findChatByGroup: (state) => (gid) => {
			let chats = state.findChats();
			return chats.find(chat => chat.groupID == gid)
		},
		findMessage: (state) => (chat, msgInfo) => {
			if (!chat || !Array.isArray(chat.messages)) {
				return null;
			}
			if (msgInfo.id) {
				for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
					let m = chat.messages[idx];
					if (m.id && msgInfo.id == m.id) {
						return m;
					}
					// 如果id比要查询的消息小，说明没有这条消息
					// Continue searching - messages may not be sorted by ID
				}
			}
			// 正在发送中的临时消息可能没有id,只有tmpID
			if (msgInfo.isWX && msgInfo.tmpID) {
				for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
					let m = chat.messages[idx];
					if (!m.isWX || !m.tmpID) {
						continue;
					}
					if (msgInfo.tmpID == m.tmpID) {
						return m;
					}
					// 如果id比要查询的消息小，说明没有这条消息
					// Continue searching - messages may not be sorted by tmpID
				}
			}
		},
		// 按分组聚合会话：{ [groupLabelID]: { pinned, normal, unreadCount, chatCount } }
		// Pinia getter 只在 state.chats 引用变化时重算，避免每次属性变更都 O(n) 遍历
		chatsByGroup: (state) => {
			const map = {};
			const sortFn = (a, b) => b.latestMsgSendTime - a.latestMsgSendTime;
			state.chats.forEach(chat => {
				if (!chat || !chat.conversationID || chat.delete) return;
				const gid = effectiveGroupID(chat);
				if (!map[gid]) {
					map[gid] = { pinned: [], normal: [], unreadCount: 0, chatCount: 0 };
				}
				map[gid].chatCount++;
				// 免打扰会话未读不参与分组未读（与 _groupSummary/Home.vue 口径一致）
				if (!chat.isDnd) {
					map[gid].unreadCount += chat.unreadCount || 0;
				}
				if (chat.isPinned) {
					map[gid].pinned.push(chat);
				} else {
					map[gid].normal.push(chat);
				}
			});
			Object.values(map).forEach(g => {
				g.pinned.sort(sortFn);
				g.normal.sort(sortFn);
			});
			return map;
		},
	}
});